/**
 * Client-side auth initialization plugin
 *
 * Runs on the client after Pinia is initialized. Two responsibilities:
 *
 *   1. Mark the auth store as hydrated so the route middleware knows
 *      the persisted state (token, last-known user) has finished
 *      reading from localStorage. This prevents a flash of "redirect
 *      to login" while the persistence plugin is still warming up.
 *
 *   2. Refresh the user profile (and therefore permissions) from the
 *      API on every app boot, even when a cached `user` already
 *      exists in the persisted state.
 *
 *      Why this matters: `permissions` live on the persisted `user`
 *      object. Without this refresh, an admin revoking access from a
 *      logged-in operator would not take effect until the operator
 *      logged out and back in — meanwhile the operator keeps seeing
 *      menu items / pages they shouldn't, because `useCan` reads
 *      from the stale snapshot. The fetch runs in the background so
 *      the cached snapshot still drives the first paint; once the
 *      API replies, reactive consumers (sidebar nav, page guards)
 *      re-render with the up-to-date abilities.
 *
 *      `fetchProfile` already handles a stale token internally
 *      (calls `refreshAccessToken` on 401, logs out on hard failure),
 *      so we don't need extra error handling here beyond a swallow.
 *
 * Not handled here:
 *   - Refreshing on tab focus / visibility change. If an operator
 *     keeps the tab open while permissions change server-side they
 *     still see the stale menu until they reload. We can layer a
 *     `visibilitychange` refresh on top of this if/when that gap
 *     becomes operationally annoying.
 */
export default defineNuxtPlugin({
  name: 'auth-init',
  // Run after pinia-persistence plugin to ensure store is ready
  dependsOn: ['pinia-persistence'],
  setup() {
    const authStore = useAuthStore()

    nextTick(() => {
      // Before hydration is marked: pages fetch as soon as it is, and they do
      // not refetch when the tenant changes underneath them.
      if (authStore.user && hostTenantPath()) {
        const tenantStore = useTenantStore()
        const visible = visibleTenants(authStore.user.tenants)
        if (!visible.length) {
          authStore.clearAuth()
        } else if (!visible.some(t => t.tenantId === tenantStore.currentTenantId)) {
          tenantStore.setCurrentTenant(visible[0]!.tenantId)
        }
      }

      authStore.markHydrated()

      if (authStore.accessToken) {
        authStore.fetchProfile().catch(() => {
          // Stale / invalid token paths are handled inside
          // `fetchProfile` itself (refresh-then-clear). Anything that
          // bubbles up here is a transient network error — keep the
          // cached snapshot rather than blanking the UI.
        })
      }
    })
  }
})
